import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import {
  getChatHistoryDisplayDate,
  parseFullMessage,
} from 'src/app/common/utilities/glai-helpers';
import { FilterList } from '../../shared/components/applied-filters/applied-filters.dto';
import { GlaiPreviousChatsModalComponent } from '../../shared/components/glai/glai-previous-chats-modal/glai-previous-chats-modal.component';
import { SmartAssistantFeedFilterModalComponent } from '../../shared/components/smart-assistant-feed/smart-assistant-feed-filter-modal/smart-assistant-feed-filter-modal.component';
import {
  ChatFeedMessage,
  emptySmartAssistantFeedFilters,
  FeedConversation,
  GlaiMessageDTO,
  ParsedFeedFullMessage,
  SmartAssistantFeedFilterDTO,
  smartAssistantFeedFilterDTOToSmartAssistantFeedPayloadItemsDTO,
  SmartAssistantFeedPayloadItemsDTO,
} from '../../shared/dtos/glai.dto';
import { GlaiFeedService } from '../../shared/services/glai/glai-feed.service';
import { GlaiService } from '../../shared/services/glai/glai.service';

@Component({
  selector: 'app-smart-assistant-feed-page',
  standalone: false,
  templateUrl: './smart-assistant-feed-page.component.html',
  styleUrl: './smart-assistant-feed-page.component.scss',
})
export class SmartAssistantFeedPageComponent implements OnInit, OnDestroy {
  @ViewChild('smartAssistantFeedFilterModal')
  smartAssistantFeedFilterModal: SmartAssistantFeedFilterModalComponent;

  subscriptions = new Subscription();

  convosLoading = false;

  conversationLoading = false;

  displayFilterModal = false;

  conversations: FeedConversation[];

  conversationId: number | undefined;

  selectedConversation: FeedConversation | null = null;

  detailedFeedMessages: ChatFeedMessage[];

  showDataPanel: boolean;

  // FILTERING
  smartAssistantFeedFilters: SmartAssistantFeedFilterDTO;

  smartAssistantFeedSearchOptions: SmartAssistantFeedPayloadItemsDTO = {
    per_page: 10,
    sort_order: 'DESC',
    page: 1,
  };

  // SEARCH
  searchTerm = '';

  searchTimeout: ReturnType<typeof setTimeout>;

  feedMeta: PaginationDTO;

  getChatHistoryDisplayDate = getChatHistoryDisplayDate;

  constructor(
    private glaiFeedService: GlaiFeedService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.showDataPanel = false;
    this.smartAssistantFeedFilters = emptySmartAssistantFeedFilters();
    this.loadConversations();

    this.route.params.subscribe((params) => {
      this.conversationId = parseInt(params['convoId']);
      this.getConversation(parseInt(params['convoId']));
    });
  }

  resetConvoId() {
    this.conversationId = undefined;
  }

  setFilters(filters?: SmartAssistantFeedFilterDTO) {
    if (filters) {
      this.smartAssistantFeedFilters = filters;
      this.smartAssistantFeedFilters.search = this.searchTerm;
    }
    this.createPayload();
    this.loadConversations();
  }

  removeFilter(keyName: string) {
    /* eslint-disable-next-line default-case */
    switch (keyName) {
      case 'search':
        this.smartAssistantFeedFilters.search = '';
        this.searchTerm = '';
        break;
      case 'district':
        this.smartAssistantFeedFilters.district = null;
        this.smartAssistantFeedFilters.schoolList = null;
        this.smartAssistantFeedFilters.userList = null;
        this.smartAssistantFeedFilters.fromDate = null;
        this.smartAssistantFeedFilters.toDate = null;
        break;
      case 'date':
        this.smartAssistantFeedFilters.fromDate = null;
        this.smartAssistantFeedFilters.toDate = null;
        break;
    }
    this.setFilters();
  }

  removeFilterFromList(filterList: FilterList) {
    /* eslint-disable-next-line default-case */
    switch (filterList.keyName) {
      case 'schools':
        if (this.smartAssistantFeedFilters.schoolList) {
          this.smartAssistantFeedFilters.schoolList =
            this.smartAssistantFeedFilters.schoolList.filter(
              (item) => item !== filterList.value
            );
        }
        break;
      case 'user_id':
        if (this.smartAssistantFeedFilters.userList) {
          this.smartAssistantFeedFilters.userList =
            this.smartAssistantFeedFilters.userList.filter(
              (item) => item !== filterList.value
            );
        }
        break;
    }
    this.setFilters();
  }

  updateSearchPayload() {
    this.smartAssistantFeedFilters.search = this.searchTerm;
    this.createPayload();
  }

  createPayload() {
    this.smartAssistantFeedSearchOptions =
      smartAssistantFeedFilterDTOToSmartAssistantFeedPayloadItemsDTO(
        this.smartAssistantFeedFilters
      );
  }

  handlePageChange() {
    this.createPayload();
    this.loadConversations();
  }

  showFilterModal() {
    this.displayFilterModal = true;
  }

  closeFilterModal() {
    this.displayFilterModal = false;
  }

  loadConversations() {
    this.convosLoading = true;
    this.subscriptions.add(
      this.glaiFeedService
        .getConversations(this.smartAssistantFeedSearchOptions)
        .subscribe({
          next: (response) => {
            if (response.items) {
              this.conversations = response.items;
              this.feedMeta = response._meta;
              this.convosLoading = false;
              if (!this.conversationId && this.conversations[0]) {
                this.getConversation(this.conversations[0].id);
              }
            }
          },
          error: (error) => {
            // eslint-disable-next-line no-console
            console.error('Error fetching conversations:', error);
            this.convosLoading = false;
          },
        })
    );
  }

  getConversation(id: number) {
    this.router.navigate([`/smart-assistant-feed/${id}`]);
    this.conversationLoading = true;
    this.glaiFeedService.getConversation(id).subscribe({
      next: (response) => {
        this.selectedConversation = response.item;
        this.conversationLoading = false;

        const conversationMessages = response.item.messages;

        this.detailedFeedMessages = conversationMessages.map((message) =>
          this.mapGlaiMessageToChatFeedMessage(message)
        );
      },
      error: () => {
        this.conversationLoading = false;
      },
    });
  }

  mapGlaiMessageToChatFeedMessage(message: GlaiMessageDTO): ChatFeedMessage {
    const parsedFullMessage = parseFullMessage(message.full_message);
    const content =
      message.text.trim() || parsedFullMessage?.message?.trim() || '';

    const mappedMessage: ChatFeedMessage = {
      id: `${message.id}`,
      content,
      rawContent: content,
      isComplete: true,
      created_at: message.created_at,
      role: message.role === 'assistant' ? 'assistant' : 'user',
      files: GlaiPreviousChatsModalComponent.extractFiles(parsedFullMessage),
      user_feedback: message.user_feedback ?? null,
      parsed_full: this.parseFeedFullMessage(message.full_message),
    };

    if (mappedMessage.role === 'assistant') {
      GlaiService.extractThinking(mappedMessage);
    }

    return mappedMessage;
  }

  // eslint-disable-next-line class-methods-use-this
  private parseFeedFullMessage(fullMessage: string) {
    return JSON.parse(fullMessage) as ParsedFeedFullMessage;
  }

  // SEARCH
  searchTermChanged(event: Event) {
    this.searchTerm = (<HTMLInputElement>event.target).value;
    this.smartAssistantFeedFilters.page = 1;
    this.updateSearchPayload();
    this.searchDebounce();
  }

  searchDebounce() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.loadConversations();
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.subscriptions.unsubscribe();
  }
}
